// src/store/dataSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { APIGetData } from '../APIS/wupuapAPI.js';
import axios from 'axios';

const BASE_URL = 'https://portal.ttpadvisory.co.uk:3556/api/crm';

const initialState = {
  columnSetupDone: false,
  records: [],
  customerEmails: [],
  loadingEmails: false,
  errorEmails: null,
  columns: "",
  loading: false,
  error: null,
  pageSize: 25,
  page: 0,
  rowCount: 0,
  totalCount: 0,
  forwardCursor: null,
  backwardCursor: null,
  orderBy: { field: 'date_created', sort: 'desc' },
  filterBy: {
    items: [],
    logicOperator: 'and',
    quickFilterLogicOperator: 'and',
    quickFilterValues: []
  },
  auth: {},
  selectedRow: null,
  selectedColumn: null,
  columnModel: null,
  viewRecord: false,
  editRecord: false,
  viewNotes: false,
  viewPhoneCalls: false,
  viewCustomerEmails: false,
  rowdata: {},
  selectedId: null,
  loaded: false,
  loadingFields: {},
  editedRows: {},
  undoStack: {}
};

export const fetchRecords = createAsyncThunk(
  'data/fetchRecords',
  async (_, { getState, rejectWithValue }) => {
    const state = getState().data;
    const {
      pageSize,
      forwardCursor,
      backwardCursor,
      orderBy,
      filterBy,
      auth
    } = state;

    try {
      const response = await APIGetData({
        token: auth.token,
        tablename: 'ttp_leads',
        forwardCursor,
        backwardCursor,
        limit: pageSize,
        orderBy,
        filterBy
      });

      return response;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const batchUpdateFields = createAsyncThunk(
  'data/batchUpdateFields',
  async ({ ids, field, value }, { dispatch, getState, rejectWithValue }) => {
    const token = getState().data.auth.token;

    const idsArray = Array.isArray(ids) ? ids : [ids];
console.log(`batchUpdateField: `, idsArray)
    try {
      idsArray.forEach(id => {
        dispatch(updateRecordOptimistic({ id, field, value }));
      });

      await axios.patch(
        'https://portal.ttpadvisory.co.uk:3556/api/crm/patch',
        { ids: idsArray, field, value },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      idsArray.forEach(id => {
        dispatch(confirmRecordUpdate({ id, field }));
      });
    } catch (error) {
      idsArray.forEach(id => {
        dispatch(revertRecordUpdate({ id, field }));
      });
      return rejectWithValue('Batch update failed');
    }
  }
);


export const fetchCustomerEmails = createAsyncThunk(
  'data/fetchCustomerEmails',
  async (uid, { getState, rejectWithValue }) => {
    try {
      const token = getState().data.auth.token;
      const response = await axios.get(`${BASE_URL}/customer-emails/${uid}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    setColumns: (state,action) => {
      state.columns = action.payload
    },
    markColumnsInitialized: (state,action) => {
      state.columnSetupDone = action.payload;
    },
    setAuth: (state, action) => { state.auth = action.payload; },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 0;
      state.forwardCursor = null;
      state.backwardCursor = null;
    },
    setPage: (state,action) => {state.page = action.payload; },
    setOrderBy: (state, action) => {
      state.orderBy = action.payload;
      state.forwardCursor = null;
      state.backwardCursor = null;
      state.page = 0;
    },
    setFilterBy: (state, action) => { state.filterBy = action.payload; },
    goForward: (state, action) => {
      state.backwardCursor = null;
      state.forwardCursor = action.payload;
    },
    goBackward: (state, action) => {
      state.forwardCursor = null;
      state.backwardCursor = action.payload;
    },
    resetCursors: (state) => {
      state.forwardCursor = null;
      state.backwardCursor = null;
      state.page = 0
    },
    updateRecord: (state, action) => {
      const { id, field, value } = action.payload;
      const index = state.records.findIndex(row => row.id === id);
      if (index !== -1) {
        state.records[index][field] = value;
      }
    },
    updateWholeRecord: (state, action) => {
      const { id, record } = action.payload;
      const index = state.records.findIndex(row => row.id === id);
      if (index !== -1) {
        state.records[index] = { ...state.records[index], ...record };
      }
    },
    editRowField: (state, action) => {
      const { id, field, value } = action.payload;
      if (!state.editedRows[id])
        state.editedRows[id] = {};
      state.editedRows[id][field] = value;
    },
    discardEdit: (state, action) => {
      delete state.editedRows[action.payload];
    },
    updateRecordOptimistic: (state, action) => {
      const { id, field, value } = action.payload;
      const rowIndex = state.records.findIndex(row => row.id === id);
      if (rowIndex >= 0) {
        const prevValue = state.records[rowIndex][field];
        state.undoStack[`${id}-${field}`] = prevValue;
        state.records[rowIndex][field] = value;
        state.loadingFields[`${id}-${field}`] = true;
      }
    },
    confirmRecordUpdate: (state, action) => {
      const { id, field } = action.payload;
      delete state.loadingFields[`${id}-${field}`];
      delete state.undoStack[`${id}-${field}`];
    },
    revertRecordUpdate: (state, action) => {
      const { id, field } = action.payload;
      const key = `${id}-${field}`;
      const rowIndex = state.records.findIndex(row => row.id === id);
      if (rowIndex >= 0 && state.undoStack[key] !== undefined) {
        state.records[rowIndex][field] = state.undoStack[key];
      }
      delete state.loadingFields[key];
      delete state.undoStack[key];
    },
    editMultipleFields: (state, action) => {
      const { ids, field, value } = action.payload;
      ids.forEach(id => {
        if (!state.editedRows[id]) state.editedRows[id] = {};
        state.editedRows[id][field] = value;
      });
    },
    discardMultipleEdits: (state, action) => {
      action.payload.forEach(id => {
        delete state.editedRows[id];
      });
    },
    selectRow: (state, action) => { state.selectedRow = action.payload; },
    selectColumn: (state, action) => { state.selectedColumn = action.payload; },
    selectModel: (state, action) => { state.columnModel = action.payload; },
    setViewRecord: (state, action) => { state.viewRecord = action.payload; },
    setEditRecord: (state, action) => { state.editRecord = action.payload; },
    setViewNotes: (state, action) => { state.viewNotes = action.payload; },
    setViewPhoneCalls: (state, action) => { state.viewPhoneCalls = action.payload; },
    setViewCustomerEmails: (state, action) => {state.viewCustomerEmails = action.payload; },
    setRowData: (state, action) => { state.rowdata = action.payload; },
    setSelectedId: (state, action) => { state.selectedId = action.payload.id; },
    startEditingRow: (state, action) => {
      const id = action.payload;
      if (!state.editedRows[id]) {
        state.editedRows[id] = {};
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecords.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecords.fulfilled, (state, action) => {
        const items = action.payload.items || [];
        state.records = items.map(item => ({ ...item, _fadeIn: true }));
        // state.records = items;
        state.rowCount = items.length;
        state.totalCount = action.payload.totalCount;
        state.forwardCursor = action.payload.nextForwardCursor || null;
        state.backwardCursor = action.payload.nextBackwardCursor || null;
        state.loading = false;
        state.loaded = true;
      })
      .addCase(fetchRecords.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Fetch failed';
      })
      .addCase(batchUpdateFields.rejected, (state, action) => {
        state.error = action.payload || 'Batch update failed';
      })
      .addCase(fetchCustomerEmails.pending, (state) => {
        state.loadingEmails = true;
        state.errorEmails = null;
      })
      .addCase(fetchCustomerEmails.fulfilled, (state, action) => {
        state.customerEmails = action.payload;
        state.loadingEmails = false;
      })
      .addCase(fetchCustomerEmails.rejected, (state, action) => {
        state.loadingEmails = false;
        state.errorEmails = action.payload;
      });

  }
});

export const {
  setAuth,
  setPageSize,
  setPage,
  setOrderBy,
  setFilterBy,
  goForward,
  goBackward,
  resetCursors,
  updateRecord,
  updateWholeRecord,
  updateRecordOptimistic,
  confirmRecordUpdate,
  revertRecordUpdate,
  editRowField,
  discardEdit,
  editMultipleFields,
  discardMultipleEdits,
  selectRow,
  selectColumn,
  selectModel,
  setViewRecord,
  setEditRecord,
  setViewNotes,
  setViewPhoneCalls,
  setViewCustomerEmails,
  setRowData,
  setSelectedId,
  startEditingRow,
  markColumnsInitialized,
  setColumns
} = dataSlice.actions;

export default dataSlice.reducer;